import { useCallback, useEffect, useMemo, useReducer, useState, useSyncExternalStore } from "react";
import { type AlertMute, alertDecision, alertItems, muteItems } from "../ordering/alerts";
import { usePlatform } from "../provider";
import { useLiveBoard } from "./use-live-board";
import { useCan } from "./use-navigation";

/**
 * The new-order and service-request alert (OP-03; realtime-notifications.md,
 * Alerting staff). Call it once, in the app shell, so it sounds on every
 * tab: it loops the platform's alert sound while a QR batch waits for a
 * decision or a table's call is not acknowledged, and stops when someone
 * acts on it, on any device. `mute()` silences what is waiting now for 5
 * minutes; anything new sounds again. The returned counts draw the banner.
 * Roles that cannot act (the kitchen) get no alert.
 */
export function useOrderAlerts() {
	const platform = usePlatform();
	const decideBatches = useCan("batch.decide");
	const handleRequests = useCan("order.edit");
	const enabled = decideBatches || handleRequests;
	const { board, status } = useLiveBoard();
	const items = useMemo(
		() => (status === "ready" ? alertItems(board, { decideBatches, handleRequests }) : []),
		[board, status, decideBatches, handleRequests],
	);
	const [mute, setMute] = useState<AlertMute | null>(null);
	// Re-render when the mute runs out.
	const [, wake] = useReducer((n: number) => n + 1, 0);
	const decision = alertDecision(items, mute, Date.now());

	useEffect(() => {
		if (decision.wakeAt === null) return;
		const timer = setTimeout(wake, Math.max(0, decision.wakeAt - Date.now()));
		return () => clearTimeout(timer);
	}, [decision.wakeAt]);

	useEffect(() => {
		if (decision.sound) platform.alerts.start(decision.sound);
		else platform.alerts.stop();
	}, [decision.sound, platform]);
	useEffect(() => () => platform.alerts.stop(), [platform]);

	return {
		/** The role gets alerts at all. */
		enabled,
		newOrders: decision.newOrders,
		requests: decision.requests,
		/** Something is waiting: show the banner. */
		waiting: decision.newOrders + decision.requests > 0,
		sounding: decision.sound !== null,
		sound: decision.sound,
		muted: decision.muted,
		/** Silences what is waiting now for 5 minutes. */
		mute: useCallback(() => setMute(muteItems(items, Date.now())), [items]),
		unmute: useCallback(() => setMute(null), []),
	};
}

export type OrderAlerts = ReturnType<typeof useOrderAlerts>;

const noSubscription = () => () => {};

/**
 * Whether the alert sound may play. On the web the browser allows it only
 * after a click or key press on the page, so the app asks for one at sign-in
 * ("Turn on sound"); `allow()` must run inside that click. Always allowed on
 * Android.
 */
export function useSoundPermission() {
	const sound = usePlatform().alerts.sound;
	const allowed = useSyncExternalStore(
		sound ? (listener) => sound.subscribe(listener) : noSubscription,
		() => sound?.allowed() ?? true,
		() => sound?.allowed() ?? true,
	);
	return {
		allowed,
		allow: useCallback(() => (sound ? sound.allow() : Promise.resolve(true)), [sound]),
	};
}
